import {useMemo} from 'react'
import {queryFnFetchWithHeaders} from '@github-ui/react-core/future/query-fn-fetch'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  type QueryKey,
  useSuspenseInfiniteQuery,
  type InfiniteData,
  type UseSuspenseInfiniteQueryResult,
} from '@github-ui/react-query'
import {
  type TimelineItem as ProtoTimelineItem,
  TimelineResponseSchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import {schemaHash} from '@github-ui/dotcom-schema/protobuf'
import {createPersister} from '@github-ui/react-query-persister'
import type {Validator} from '@github-ui/safe-indexed-db-storage'
import {useAnalytics} from '@github-ui/use-analytics'
import {parseLinkHeader} from '../../utils/parse-link-header'

export function timelineQueryKey(pathName: string): QueryKey {
  return [PageData.timeline, pathName]
}

export type TimelinePage = {
  data: ProtoTimelineItem[]
  nextUrl: string | null
}

export function timelineApiUrl(pathName: string): string {
  return `${pathName}/page_data/${PageData.timeline}`
}

function isTimelinePage(value: unknown): value is TimelinePage {
  if (typeof value !== 'object' || value === null) return false
  const page = value as Record<string, unknown>
  return Array.isArray(page.data) && (typeof page.nextUrl === 'string' || page.nextUrl === null)
}

// Validates the `InfiniteData` wrapper TanStack persists for this infinite
// query: `{pages: TimelinePage[], pageParams: unknown[]}`.
function isPersistedTimelineData(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false
  const data = value as Record<string, unknown>
  return Array.isArray(data.pages) && data.pages.every(isTimelinePage) && Array.isArray(data.pageParams)
}

// Reuse the proto schema's fingerprint for cache-busting (mirrors
// `createProtoValidator`): when `TimelineResponseSchema` changes, the hash
// changes and previously cached entries become unreachable. The structural
// `Check` only guards the bespoke page shape — `data` items are covered by the
// schema hash, while `nextUrl` is derived from the Link header so we validate
// it explicitly.
const timelinePersisterValidator: Validator = {
  Check: isPersistedTimelineData,
  Code: () => `proto:${schemaHash(TimelineResponseSchema)}`,
  Errors: () => [],
}

export function useInfiniteTimelinePageData({
  pathName,
}: {
  pathName: string
}): UseSuspenseInfiniteQueryResult<InfiniteData<TimelinePage>> {
  const {sendAnalyticsEvent} = useAnalytics()
  const persister = useMemo(
    () =>
      createPersister({
        validator: timelinePersisterValidator,
        sendAnalyticsEvent,
        alwaysRefetch: false,
      }),
    [sendAnalyticsEvent],
  )

  return useSuspenseInfiniteQuery<TimelinePage, Error, InfiniteData<TimelinePage>, QueryKey, string>({
    queryKey: timelineQueryKey(pathName),
    initialPageParam: timelineApiUrl(pathName),
    queryFn: async ({pageParam}: {pageParam: string}): Promise<TimelinePage> => {
      const {result, headers} = await queryFnFetchWithHeaders<typeof TimelineResponseSchema>(
        {routeId: PageData.timeline, queryName: 'useInfiniteTimelinePageData', queryDeps: {pathname: pageParam}},
        {schema: TimelineResponseSchema},
      )
      const nextUrl = parseLinkHeader(headers.get('Link'), 'next')
      return {data: result.items, nextUrl}
    },
    getNextPageParam: (lastPage: TimelinePage) => {
      return lastPage.nextUrl ?? undefined
    },
    staleTime: Infinity,
    // we need a custom persister because the queryFn augments the proto response with nextUrl.
    persister,
  })
}
